import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { WPRangedInput } from "../WPInput";

import { EndashIcon, PlusIcon, WarningOutlinedIcon } from "../../assets/icons";

import { isNullOrUndefined } from "../../utils/appUtils";

import "./WPCounterInput.scss";

const WPCounterInput = ({
  value: InitialValue,
  step = 1,
  ranges,
  onChange,
  disabled,
  prefix = true,
  suffix = true,
  prefixDisabled,
  suffixDisabled,
  showToolTip,
  onValidationError,
  ...props
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(InitialValue);
  const [errMsg, setErrMessage] = useState("");
  const [isHovered, setIsHovered] = useState(false);

  const { min: minRange, max: maxRange } = ranges?.hard || ranges?.soft || {};

  useEffect(() => {
    setValue(InitialValue);
  }, [InitialValue]);

  const handleValueChange = (operation) => {
    const currentValue = parseFloat(value);
    // Return early if value is not a valid number
    if (isNaN(currentValue)) {
      return;
    }

    const nextValue =
      operation === "suffix" ? currentValue + step : currentValue - step;

    if (
      disabled ||
      (operation === "suffix" &&
        !isNullOrUndefined(maxRange) &&
        nextValue > maxRange) ||
      (operation === "prefix" &&
        !isNullOrUndefined(minRange) &&
        nextValue < minRange)
    ) {
      return;
    }

    setValue(nextValue);
    onChange?.({ target: { value: nextValue, id: props.id } });
  };

  // const updateValue = nextValue => {
  //   // Accept empty string for controlled input, or valid number
  //   if (nextValue === "" || isNaN(Number(nextValue))) {
  //     setValue(nextValue);
  //     return;
  //   }
  //   const num = Number(nextValue);
  //   setValue(num);
  //   if ((!isNullOrUndefined(maxRange) && num > maxRange) || (!isNullOrUndefined(minRange) && num < minRange)) {
  //     return;
  //   }
  //   onChange?.({ target: { value: num } });
  // };

  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    setValue(inputValue);
    onChange?.(event);
  };

  const handleValidationError = (error, id, testId) => {
    setErrMessage(error);
    onValidationError?.(error, id, testId);
  };

  const currentValue = parseFloat(value);
  const isValidNumber = !isNaN(currentValue);

  // If prefixDisabled/suffixDisabled are explicitly provided, use them
  // Otherwise, compute based on current value and ranges
  const checkPrefixDisabled =
    isValidNumber && !isNullOrUndefined(minRange) && currentValue <= minRange;
  const checkSuffixDisabled =
    isValidNumber && !isNullOrUndefined(maxRange) && currentValue >= maxRange;

  const isSuffixDisabled =
    suffixDisabled !== undefined ? suffixDisabled : checkSuffixDisabled;
  const isPrefixDisabled =
    prefixDisabled !== undefined ? prefixDisabled : checkPrefixDisabled;

  const counterButtons = [
    { type: "prefix", Icon: EndashIcon, isDisabled: isPrefixDisabled },
    { type: "suffix", Icon: PlusIcon, isDisabled: isSuffixDisabled },
  ];

  const renderButton = (operation, opacity = 1) => {
    const { type, Icon, isDisabled } = operation;
    return (
      <button
        key={type}
        className={`icon-button ${isDisabled ? "disabled" : ""}`}
        onClick={() => handleValueChange(type)}
        disabled={disabled || isDisabled}
      >
        <Icon opacity={opacity} />
      </button>
    );
  };

  return (
    <div
      className="wp-counter-input-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showToolTip && errMsg && isHovered && (
        <div className="wp-counter-tooltip-error">
          <WarningOutlinedIcon className="error-icon" />
          {errMsg}
        </div>
      )}
      <WPRangedInput
        integer
        value={value}
        onChange={handleInputChange}
        prefix={prefix && renderButton(counterButtons[0])}
        suffix={suffix && renderButton(counterButtons[1], 0.7)}
        disabled={disabled}
        ranges={ranges}
        {...props}
        requiredMsg={t("ValueStrings.requiredField")}
        onValidationError={handleValidationError}
      />
    </div>
  );
};

export default WPCounterInput;
